import { gustReachesBox } from "@bfly/engine";
import { mulberry32, randomSeed } from "@bfly/engine/random";
import {
  SLOTS,
  canonicalPicks,
  chooseRerollSlots,
  dueRewrites,
  isSlotKey,
  nextSeed,
  recentFrom,
  remember,
  repick,
  type Picks,
  type Recent,
  type SlotKey,
} from "../content/intro-copy.js";
import { decodeText, endDecode, shownText } from "../effects/text-decode.js";
import { motionReduced } from "../page/motion.js";
import type { WaveRing } from "../waves/waves.js";
import { boxReached, channelOf, type WaveAges } from "../waves/wave-ages.js";

// How long each letter of a rewritten phrase takes to decode, in milliseconds. The heading
// takes about 40ms a letter, but its letters are much bigger.
const PLACE_MS = 25;

// The bio: the static sentence the build writes, made interactive. Each phrase becomes a button
// that rewrites it, and a wave rewrites a few as its front reaches them (`startWave()`,
// `reachWaves()`). A rewrite you ask for is announced as a status message. The paragraph itself
// isn't a live region, so a wave's rewrites stay silent.
export class JrsmIntroCopy extends HTMLElement {
  private picks: Picks = canonicalPicks();
  private readonly recent: Recent = recentFrom(this.picks);
  private seed = randomSeed() >>> 0;
  private rng = mulberry32(this.seed);
  private readonly controls = new Map<SlotKey, HTMLElement>();
  // For each live wave (by id), the slots its front hasn't reached yet.
  private readonly pending = new Map<number, Set<SlotKey>>();
  private readonly status = document.createElement("span");
  // Ends this connection's listeners.
  private connection: AbortController | null = null;

  connectedCallback(): void {
    const bio = this.querySelector("#bio");
    if (
      !(bio instanceof HTMLElement) ||
      bio.querySelectorAll("[data-slot]").length !== SLOTS.length
    )
      throw new Error("jrsm-intro-copy: the bio lacks the static sentence");
    // Added once; a reconnection keeps it.
    if (!this.status.isConnected) {
      this.status.className = "visually-hidden";
      this.status.setAttribute("role", "status");
      // A live region keeps its text whole, so the text effects don't wrap its words.
      this.status.dataset.fx = "none";
      this.append(this.status);
    }
    this.connection = new AbortController();
    const { signal } = this.connection;
    this.controls.clear();
    for (const control of bio.querySelectorAll("[data-slot]")) {
      if (!(control instanceof HTMLElement)) continue;
      const slot = control.dataset.slot;
      if (!isSlotKey(slot)) continue;
      // Native buttons are their own layout boxes and can't wrap with a sentence, so the slot
      // spans get the button role instead. Without scripts, they stay plain text.
      control.setAttribute("role", "button");
      control.tabIndex = 0;
      control.setAttribute("aria-description", "Rewrite this phrase");
      control.textContent = this.picks[slot].t;
      control.addEventListener("click", () => this.rewriteAsked(slot), { signal });
      // A double or triple click on a span selects a word or paragraph, which a native button
      // doesn't do. Dragging still selects the sentence.
      control.addEventListener(
        "mousedown",
        (event) => {
          if (event.detail > 1) event.preventDefault();
        },
        { signal },
      );
      control.addEventListener(
        "keydown",
        (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          if (event.key === "Enter" && !event.repeat) control.click();
        },
        { signal },
      );
      control.addEventListener(
        "keyup",
        (event) => {
          if (event.key !== " ") return;
          event.preventDefault();
          control.click();
        },
        { signal },
      );
      this.controls.set(slot, control);
    }
  }

  disconnectedCallback(): void {
    this.connection?.abort();
    this.connection = null;
    for (const control of this.controls.values()) endDecode(control);
    // Pending wave rewrites belong to this connection.
    this.pending.clear();
  }

  /** A wave started. A fresh epoch (new seed) picks `count` (at least 2) random slots, which get
      rewritten as the wave's front reaches them (`reachWaves()`). */
  startWave(id: number, count: number): void {
    this.seed = nextSeed(this.seed);
    this.rng = mulberry32(this.seed);
    this.pending.set(id, new Set(chooseRerollSlots(count, this.rng)));
  }

  /** Rewrites each pending slot that its wave has reached, plus any left over from waves that
      have ended. `rings` are in viewport pixels; `ages` are the field's gust ages, or null
      without the field. Call it before anything changes the page's style this frame, so
      measuring the phrases doesn't force a layout. */
  reachWaves(rings: readonly WaveRing[], ages: WaveAges | null): void {
    if (this.pending.size === 0) return;
    const due = dueRewrites(this.pending, rings, (slot, ring) => this.reached(slot, ring, ages));
    const changed = new Set<SlotKey>();
    for (const [id, slot] of due) {
      const slots = this.pending.get(id);
      slots?.delete(slot);
      if (slots?.size === 0) this.pending.delete(id);
      changed.add(slot);
    }
    for (const slot of changed) repick(this.picks, slot, this.rng, this.recent);
    this.apply([...changed]);
  }

  // A click's rewrite: a touch-up on the current epoch (no new seed), and it gets announced.
  private rewriteAsked(slot: SlotKey): void {
    repick(this.picks, slot, this.rng, this.recent);
    this.apply([slot]);
    this.status.textContent = this.picks[slot].t;
  }

  // Whether a wave has reached a slot, meaning any box of its text (it can wrap across lines).
  // With the field's ages, that's any cell of a box; otherwise, the fallback front.
  private reached(slot: SlotKey, ring: WaveRing, ages: WaveAges | null): boolean {
    const control = this.controls.get(slot);
    if (!control) return true;
    const channel = ages ? channelOf(ages, ring.id) : -1;
    for (const rect of control.getClientRects()) {
      if (ages ? boxReached(ages, channel, rect) : gustReachesBox(ring, rect)) return true;
    }
    return false;
  }

  // A rewritten phrase decodes like the heading in a page transition, at `PLACE_MS` a letter.
  private apply(changed: readonly SlotKey[]): void {
    for (const slot of changed) {
      const text = this.picks[slot].t;
      remember(this.recent, slot, text);
      const control = this.controls.get(slot);
      if (!control) continue;
      if (motionReduced()) {
        endDecode(control);
        control.textContent = text;
      } else decodeText(control, shownText(control), text, { placeMs: PLACE_MS });
    }
  }
}

customElements.define("jrsm-intro-copy", JrsmIntroCopy);
